import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from "@shared/const";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchStreamLink, TRPCClientError } from "@trpc/client";
import { TRPCContext } from "@trpc/react-query/shared";
import {
  isAbortLikeError,
  isLazyChunkLoadError,
  isReactRecoverableError,
  posthogExceptionBeforeSend,
} from "@/lib/posthogExceptions";
import { attemptClientReloadRecovery } from "@/lib/reloadRecovery";
import { PostHogProvider } from "posthog-js/react";
import type { PostHogConfig } from "posthog-js";
import { useContext, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import { syncPostHogAttribution } from "@/lib/attribution";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import "./i18n"; // Initialize i18n

if (typeof window !== "undefined" && typeof Node !== "undefined") {
  const proto = Node.prototype as Node & {
    __safeRemoveChildPatched?: boolean;
    __safeInsertBeforePatched?: boolean;
  };
  if (!proto.__safeRemoveChildPatched) {
    const originalRemoveChild = proto.removeChild;
    proto.removeChild = function removeChild<T extends Node>(child: T): T {
      if (child && child.parentNode !== this) {
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };
    proto.__safeRemoveChildPatched = true;
  }

  if (!proto.__safeInsertBeforePatched) {
    const originalInsertBefore = proto.insertBefore;
    proto.insertBefore = function insertBefore<T extends Node>(
      newNode: T,
      referenceNode: Node | null,
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        try {
          return this.appendChild(newNode) as T;
        } catch {
          return newNode;
        }
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
    proto.__safeInsertBeforePatched = true;
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault?.();
    attemptClientReloadRecovery("vite:preloadError");
  });

  window.addEventListener("error", (event) => {
    const message =
      (event.error as Error | undefined)?.message ??
      (event as ErrorEvent).message ??
      "";
    if (isReactRecoverableError(event.error ?? message)) {
      event.preventDefault?.();
      return;
    }
    if (isLazyChunkLoadError(event.error ?? message)) {
      attemptClientReloadRecovery(message);
    }
  });

  // Suppress benign unhandled promise rejections to prevent them from
  // triggering React 19 concurrent rendering recovery (error #520).
  window.addEventListener("unhandledrejection", (event) => {
    const error = event.reason;
    if (isAbortLikeError(error)) {
      event.preventDefault();
      return;
    }
    if (error instanceof DOMException && error.name === "NotSupportedError") {
      event.preventDefault();
      return;
    }
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      event.preventDefault();
    }
  });
}

const posthogOptions = {
  api_host: "/ingest",
  ui_host: "https://us.posthog.com",
  defaults: "2025-05-24",
  // Cost control (Aug 2026 PostHog bill spike): autocapture was ~55% of all
  // billed events and web vitals another ~7%; custom events cover our funnels.
  autocapture: false,
  capture_performance: { web_vitals: false },
  capture_pageview: true,
  capture_pageleave: true,
  before_send: posthogExceptionBeforeSend,
  capture_exceptions: {
    capture_unhandled_errors: true,
    capture_unhandled_rejections: true,
    capture_console_errors: false,
  },
  debug: import.meta.env.MODE === "development",
  loaded: (posthog) => {
    syncPostHogAttribution(posthog);
  },
  error_tracking: {
    captureExtensionExceptions: false,
  },
} satisfies Partial<PostHogConfig>;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry(failureCount, error) {
        // Don't retry aborted requests (user navigated away)
        if (isAbortLikeError(error)) return false;
        return failureCount < 3;
      },
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe((event) => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    if (isAbortLikeError(error)) return; // User navigated away, benign
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe((event) => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    if (isAbortLikeError(error)) return; // User navigated away, benign
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchStreamLink({
      url: "/api/trpc",
      transformer: superjson,
      // Send queries as POST: large query inputs (e.g. Director estimateCost
      // with a full-song shot list) overflow Node's 16KB request-head cap
      // when URL-encoded into a GET. Server side: allowMethodOverride.
      methodOverride: "POST",
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        }).then((response) => {
          const ct = response.headers.get("content-type") ?? "";
          if (ct.includes("text/html")) {
            throw new Error(
              "The server is temporarily unavailable. Please try again in a moment.",
            );
          }
          return response;
        });
      },
    }),
  ],
});

// During React 19 concurrent error recovery, context providers may not have
// re-established their values yet. This guard prevents children from rendering
// (and crashing on trpc.useUtils()) when the tRPC context is transiently null.
function TRPCReady({ children }: { children: ReactNode }) {
  const ctx = useContext(TRPCContext);
  if (!ctx) return null;
  return children;
}

const root = createRoot(document.getElementById("root")!, {
  onRecoverableError(error, errorInfo) {
    if (isAbortLikeError(error) || isReactRecoverableError(error)) return;

    if (import.meta.env.DEV) {
      console.warn("[React Recoverable Error]", error, errorInfo);
    }
  },
});

root.render(
  <PostHogProvider
    apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
    options={posthogOptions}
  >
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <TRPCReady>
          <App />
        </TRPCReady>
      </QueryClientProvider>
    </trpc.Provider>
  </PostHogProvider>,
);
